/**
 * Pure section-kind resolution for the generic create-form's
 * progressive-disclosure layout. No React imports — safe to unit test in
 * isolation and to import from data-layer code.
 */

import type { EntityFormSectionConfig } from "@/lib/types/forms/entity-form-sections";
import type { SimpleFieldConfig } from "@/lib/types/table/field-config-types";

/** True when any field listed in the section is zod-required. */
export function sectionHasRequiredField(
  section: EntityFormSectionConfig,
  fieldsByName: Map<string, SimpleFieldConfig>
): boolean {
  return section.fieldNames.some((name) => {
    const f = fieldsByName.get(name);
    return !!(f && (f.required === true || f.validation?.required));
  });
}

/**
 * Resolve the effective render kind for a section.
 *
 * A section that contains a REQUIRED field is ALWAYS forced to `"always"`
 * (a visible card), regardless of how the entity config declared `kind`. A
 * collapsed accordion section hides its required field, so the user never sees
 * the field is required and submit silently fails validation. Required fields
 * must always be visible. Non-required sections keep their declared kind.
 */
export function resolveSectionKind(
  section: EntityFormSectionConfig,
  fieldsByName: Map<string, SimpleFieldConfig>
): EntityFormSectionConfig["kind"] {
  return sectionHasRequiredField(section, fieldsByName) ? "always" : section.kind;
}
